import React, { CSSProperties, useEffect, useRef, useState } from 'react';
import { createPortal } from 'react-dom';
import { useDispatch } from 'react-redux';
import classNames from 'classnames';
// Utils
import { hideTooltip } from './tooltipActions';
import { DEFAULT_TOOLTIP_INSETS } from './tooltipUtil';
import { getFloatingPanelMountPoint } from './floatingPanelUtils';
import usePrevious from '../../utils/react/usePrevious';
import { useFloatingPanelPosition } from './useFloatingPanelPosition';

// Components
import FloatingPanelErrorBoundary from './FloatingPanelErrorBoundary';
import FloatingPanelArrow from './FloatingPanelArrow';
import { CSSTransitionGroup } from '../../../node_module_clones/react-transition-group';

// Types
import { HIDDEN_POSITION, TooltipAlignment, TooltipPositions } from './tooltipConstants';
import { Rect } from '../../../common/maths/geometry/rect/rectTypes';

import './FloatingPanel.scss';
import { PlatformDetails } from '../../../common/platform/platformTypes';

type FloatingPanelProps = {
    responsive?: boolean;
    selector?: string;
    mountPointSelector?: string;
    relativeParentSelector?: string;
    delay?: number;
    offset?: number;
    duration?: number;
    className?: string;
    showArrow?: boolean;
    arrowColor?: string;
    arrowStrokeColor?: string;
    arrowWidth?: number;
    arrowHeight?: number;
    arrowStrokeWidth?: number;
    arrowStyle?: React.CSSProperties;
    targetRect?: DOMRect;
    style?: React.CSSProperties;
    insets?: Rect;
    fade?: boolean;
    position?: TooltipPositions;
    alignment?: TooltipAlignment;
    children?: React.ReactElement;
    pollPosition?: boolean;
    hideOnEvent?: string;
    tooltipId?: string;
    dispatchHideTooltip?: () => void;
    onMouseDown?: () => void;
    onClick?: () => void;
    gridSize?: number;
    distance?: number;

    // This will determine whether the panel should be shown or not
    // NOTE: Changing this to false won't trigger a HIDE_TOOLTIP redux action
    show?: boolean;

    platformDetails?: PlatformDetails;
};

const FloatingPanel = (props: FloatingPanelProps) => {
    const {
        children,
        className,
        fade,
        style,
        arrowColor = 'var(--arrow-color)',
        arrowStrokeColor = 'var(--arrow-stroke-color)',
        arrowWidth = 9,
        arrowHeight = 7,
        arrowStrokeWidth = 1,
        responsive,
        onMouseDown,
        onClick,
        mountPointSelector,
        hideOnEvent = 'click',
        tooltipId,
        selector,
        delay,
        duration,
        insets = DEFAULT_TOOLTIP_INSETS,
        show = true,
    } = props;

    const dispatch = useDispatch();

    const panelRef = useRef<HTMLDivElement>(null);
    const arrowRef = useRef<HTMLDivElement>(null);
    const portalMountPointRef = useRef<HTMLElement | null>(null);
    const showPanelDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);
    const hidePanelDelayRef = useRef<ReturnType<typeof setTimeout> | null>(null);

    const [hidden, setHidden] = useState<boolean>(true);

    const prevShow = usePrevious(show);

    const { cancelUpdatePanel, updatePanel } = useFloatingPanelPosition({
        arrowRef,
        insets,
        panelRef,
        props,
    });

    const hidePanel = () => {
        dispatch(hideTooltip(tooltipId));
    };

    const showPanel = () => {
        setHidden(false);
        updatePanel(selector);
    };

    useEffect(() => {
        if (hidden) return;

        updatePanel(selector);

        return () => {
            cancelUpdatePanel();
        };
    }, [selector]);

    useEffect(() => {
        if (!show) {
            // If show is updated from true to false, we want to hide the panel immediately
            prevShow && setHidden(true);
            return;
        }

        document.addEventListener(hideOnEvent, hidePanel, {
            capture: true,
        });

        if (delay) {
            showPanelDelayRef.current = setTimeout(showPanel, delay);
        } else {
            showPanel();
        }

        if (duration) {
            hidePanelDelayRef.current = setTimeout(hidePanel, duration);
        }

        return () => {
            document.removeEventListener(hideOnEvent, hidePanel, {
                capture: true,
            });
            if (showPanelDelayRef.current) clearTimeout(showPanelDelayRef.current);
            if (hidePanelDelayRef.current) clearTimeout(hidePanelDelayRef.current);
        };
    }, [show]);

    const tipStyle = {
        ...HIDDEN_POSITION,
        ...style,
    };

    const arrowStyle: CSSProperties = {
        ...props.arrowStyle,
        position: 'absolute',
        width: arrowWidth,
        height: arrowHeight,
    };

    const floatingPanel = !hidden ? (
        <div
            ref={panelRef}
            className={classNames('FloatingPanel', className, { hide: hidden, responsive, fade })}
            onPointerDown={onMouseDown}
            onClick={onClick}
            style={tipStyle}
        >
            <FloatingPanelArrow
                ref={arrowRef}
                width={arrowWidth}
                height={arrowHeight}
                color={arrowColor}
                strokeColor={arrowStrokeColor}
                strokeWidth={arrowStrokeWidth}
                style={arrowStyle}
            />
            <div className="content">
                <FloatingPanelErrorBoundary {...props}>{children}</FloatingPanelErrorBoundary>
            </div>
        </div>
    ) : null;

    const transitionWrapper = fade ? (
        <CSSTransitionGroup transitionName="fade-move" transitionEnterTimeout={300} transitionLeaveTimeout={300}>
            {floatingPanel}
        </CSSTransitionGroup>
    ) : (
        floatingPanel
    );

    portalMountPointRef.current = portalMountPointRef.current || getFloatingPanelMountPoint(mountPointSelector);
    if (!portalMountPointRef.current) return null;

    return createPortal(transitionWrapper, portalMountPointRef.current);
};

export default FloatingPanel;
